/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

/**
 * A row that slides sideways, and says so.
 *
 * A strip with its scrollbar hidden is indistinguishable from a row that
 * happens to be clipped — there is nothing on screen admitting that more
 * exists past the edge. The fade is that admission, and it has to follow the
 * position to be honest: fading the right edge while you are already at the
 * end points at content that is not there, which is worse than not fading at
 * all. So it is recomputed on scroll and on resize, and when everything fits
 * there is no fade at all.
 *
 * On a touch screen the finger already does this. The mouse does not, so the
 * strip is also draggable, and the wheel's vertical delta becomes horizontal
 * movement while the pointer is over it — a desktop user should not have to
 * find a scrollbar that was deliberately hidden.
 */

import { useCallback, useEffect, useRef, useState } from 'react';

/** How far the fade reaches in from an edge. */
const FADE = '24px';

/** Pixels of movement past which a press counts as a drag, not a click. */
const DRAG_THRESHOLD = 4;

/** Which edges currently have more content behind them. */
interface EdgeState {
  start: boolean;
  end: boolean;
}

/**
 * Builds the mask for the edges that have something behind them.
 *
 * Returns `undefined` rather than a fully opaque mask when nothing overflows:
 * an always-present `mask-image` promotes the element to its own layer in
 * some engines, which costs memory on a row that had no reason to be
 * composited.
 */
function maskFor({ start, end }: EdgeState): string | undefined {
  if (!start && !end) return undefined;
  const stops = [
    start ? `transparent 0, black ${FADE}` : 'black 0',
    end ? `black calc(100% - ${FADE}), transparent 100%` : 'black 100%',
  ].join(', ');
  return `linear-gradient(to right, ${stops})`;
}

interface SlidingStripResult {
  /** Attach to the scrolling element. */
  ref: React.RefObject<HTMLDivElement | null>;
  /** Spread onto the same element. */
  props: {
    className: string;
    onScroll: () => void;
    onPointerDown: (event: React.PointerEvent<HTMLDivElement>) => void;
    onWheel: (event: React.WheelEvent<HTMLDivElement>) => void;
    style: React.CSSProperties;
  };
  /**
   * Whether the last press was a drag. A click handler inside the strip must
   * consult this, or dragging the row past a button activates it on release.
   */
  wasDragged: () => boolean;
}

/**
 * Wires a horizontally scrolling strip: edge fades, mouse drag, and wheel.
 *
 * `enabled` exists because several of these rows are a wrapping grid on a
 * wide screen and only become a strip on a narrow one. Forcing `overflow-x`
 * on the grid would turn a tidy two-row layout into a one-row scroller
 * nobody asked for.
 *
 * @param enabled - Whether this row is currently a sliding strip.
 * @returns The ref to attach, the props to spread, and the drag guard.
 */
export function useSlidingStrip(enabled = true): SlidingStripResult {
  const ref = useRef<HTMLDivElement | null>(null);
  const dragged = useRef(false);
  const [edges, setEdges] = useState<EdgeState>({ start: false, end: false });

  const measure = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const max = el.scrollWidth - el.clientWidth;
    /*
     * The start is not `scrollLeft === 0`.
     *
     * With `scroll-snap-align: start` on the items and horizontal padding on
     * the container, the first snap position sits one padding in — a strip
     * padded by 16px rests at 16 and never reaches 0. Comparing to zero read
     * that as "already scrolled" and faded the left edge of a row that was
     * at its beginning, pointing at content behind it that did not exist.
     *
     * A pixel of slack on top, because fractional layout widths leave
     * `scrollLeft` a hair short of `max` at the true end.
     */
    const padding = parseFloat(getComputedStyle(el).paddingLeft) || 0;
    const next = { start: el.scrollLeft > padding + 1, end: el.scrollLeft < max - 1 };
    setEdges((current) => (
      current.start === next.start && current.end === next.end ? current : next
    ));
  }, []);

  useEffect(() => {
    if (!enabled) {
      setEdges({ start: false, end: false });
      return undefined;
    }
    measure();
    const el = ref.current;
    if (!el || typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', measure);
      return () => window.removeEventListener('resize', measure);
    }
    /* Observing the element rather than the window: the strip can change
       width because a sibling did, with no window resize involved. */
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [measure, enabled]);

  const onPointerDown = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    /* Touch scrolls natively and a pen is not a mouse; hijacking either
       would replace a good gesture with a worse imitation of it. */
    if (!enabled || event.pointerType !== 'mouse') return;
    const el = ref.current;
    if (!el) return;

    const startX = event.clientX;
    const startScroll = el.scrollLeft;
    dragged.current = false;

    const onMove = (move: PointerEvent) => {
      const delta = move.clientX - startX;
      if (Math.abs(delta) > DRAG_THRESHOLD) dragged.current = true;
      if (dragged.current) el.scrollLeft = startScroll - delta;
    };
    const onUp = () => {
      document.removeEventListener('pointermove', onMove);
      document.removeEventListener('pointerup', onUp);
    };
    document.addEventListener('pointermove', onMove);
    document.addEventListener('pointerup', onUp);
  }, [enabled]);

  const onWheel = useCallback((event: React.WheelEvent<HTMLDivElement>) => {
    const el = ref.current;
    /* Only a purely vertical wheel is translated. A trackpad's horizontal
       delta already scrolls this, and converting both would double it. */
    if (!enabled || !el || Math.abs(event.deltaY) <= Math.abs(event.deltaX)) return;
    const max = el.scrollWidth - el.clientWidth;
    if (max <= 0) return;
    el.scrollLeft += event.deltaY;
  }, [enabled]);

  return {
    ref,
    props: {
      /* WebKit's scrollbar only hides from a stylesheet, and globals.css
         already carries that utility; no need for a second one. */
      className: enabled ? 'scrollbar-none' : '',
      onScroll: measure,
      onPointerDown,
      onWheel,
      style: enabled ? {
        overflowX: 'auto',
        scrollSnapType: 'x proximity',
        cursor: edges.start || edges.end ? 'grab' : undefined,
        maskImage: maskFor(edges),
        WebkitMaskImage: maskFor(edges),
      } as React.CSSProperties : {},
    },
    wasDragged: () => dragged.current,
  };
}

/** Spread onto each child of a sliding strip. */
export const SLIDING_STRIP_ITEM: React.CSSProperties = {
  scrollSnapAlign: 'start',
  flex: 'none',
};
